/**
 * actorsService.js — thin wrapper around the admin-backend /api/actors
 * endpoint used ONLY for backfilling pre-approved BytePlus ModelArk
 * (Seedance) asset refs onto project characters that were saved before
 * the Feb 2026 Cast & World capture fix shipped.
 *
 * Why a dedicated service: the Actors Library modal fetches actors with
 * UI filters (category / gender / search) for the pick workflow. The
 * backfill flow needs a different, filter-less fetch keyed by actor id —
 * so it lives here, isolated and testable.
 */

const USER_BACKEND_URL = process.env.REACT_APP_USER_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const buildHeaders = () => {
  const apiKey = sessionStorage.getItem('apiKey');
  const userEmail = sessionStorage.getItem('userEmail');
  const headers = { 'Content-Type': 'application/json' };
  if (apiKey) headers['X-API-Key'] = apiKey;
  if (userEmail) headers['X-User-Email'] = userEmail;
  return headers;
};

/**
 * Fetch all actors (paginated server-side; we grab the max page size).
 * Returns the raw admin-backend array. Does NOT massage field names.
 */
export const fetchAllActors = async () => {
  const res = await fetch(`${USER_BACKEND_URL}/api/actors?limit=200`, {
    headers: buildHeaders(),
  });
  if (!res.ok) throw new Error(`fetchAllActors failed: ${res.status}`);
  const data = await res.json();
  return data.actors || [];
};

/**
 * Extract BytePlus ModelArk (Seedance) asset refs from a raw actor record.
 * Accepts snake_case, camelCase, and `seedance_*` aliases (matches the
 * extractor in `ActorsLibraryModal.handleSelect`). Returns null when the
 * actor has no attached asset.
 */
export const extractByteplusRefs = (actor) => {
  if (!actor || typeof actor !== 'object') return null;
  const groupId =
    actor.byteplus_asset_group_id ||
    actor.byteplusAssetGroupId ||
    actor.seedance_asset_group_id ||
    null;
  const assetId =
    actor.byteplus_asset_id ||
    actor.byteplusAssetId ||
    actor.seedance_asset_id ||
    null;
  if (!groupId || !assetId) return null;
  const useByteplusForSeedance =
    actor.use_byteplus_for_seedance ??
    actor.useByteplusForSeedance ??
    true;
  const status = actor.byteplus_asset_status || actor.byteplusAssetStatus || actor.seedance_asset_status || null;
  return { groupId, assetId, useByteplusForSeedance: Boolean(useByteplusForSeedance), status };
};

/**
 * Backfill BytePlus ModelArk refs onto an array of project characters.
 *
 * For every character that has `actorId` but is missing BOTH
 * `byteplusAssetGroupId` and `byteplusAssetId`, look up the actor on the
 * admin backend and attach the asset refs if present. Characters that
 * already have refs are left untouched (so a manual choice wins over the
 * admin backend's current attachment).
 *
 * Returns:
 *   {
 *     patchedCharacters: [...],  // same length, same order, patched in place
 *     patchedIds: Set<characterId>,  // which chars got new refs
 *     consideredCount: number,       // characters that triggered a lookup
 *   }
 *
 * Does NOT throw on fetch failure — a network error returns the input
 * array unchanged so video generation can proceed (without seedance asset
 * refs, same as the current behaviour).
 */
export const backfillByteplusForCharacters = async (characters, { logger } = {}) => {
  const log = logger || (() => {});
  if (!Array.isArray(characters) || characters.length === 0) {
    return { patchedCharacters: characters || [], patchedIds: new Set(), consideredCount: 0 };
  }

  // Short-circuit: only characters with an actorId AND no existing refs qualify.
  const candidates = characters.filter(
    (c) => c && c.actorId && !(c.byteplusAssetGroupId && c.byteplusAssetId)
  );
  if (candidates.length === 0) {
    log('⤼ Backfill skipped — all characters either lack actorId or already carry refs');
    return { patchedCharacters: characters, patchedIds: new Set(), consideredCount: 0 };
  }

  log(`⤼ Backfill considering ${candidates.length} character(s) with actorId but no byteplus refs`);

  let actors;
  try {
    actors = await fetchAllActors();
  } catch (err) {
    log(`✗ Backfill fetch failed: ${err.message} — proceeding without refs`);
    return { patchedCharacters: characters, patchedIds: new Set(), consideredCount: candidates.length };
  }

  const byId = new Map();
  for (const a of actors) {
    if (a && a.id) byId.set(a.id, a);
  }

  const patchedIds = new Set();
  const patched = characters.map((c) => {
    if (!c || !c.actorId) return c;
    if (c.byteplusAssetGroupId && c.byteplusAssetId) return c;
    const actor = byId.get(c.actorId);
    if (!actor) {
      log(`  ✗ ${c.name || c.id}: actorId=${c.actorId} not found in admin backend`);
      return c;
    }
    const refs = extractByteplusRefs(actor);
    if (!refs) {
      log(`  — ${c.name || c.id}: actor found but has no BytePlus asset attached`);
      return c;
    }
    log(`  ✓ ${c.name || c.id}: backfilled group=${refs.groupId} asset=${refs.assetId}`);
    patchedIds.add(c.id);
    return {
      ...c,
      byteplusAssetGroupId: refs.groupId,
      byteplusAssetId: refs.assetId,
      useByteplusForSeedance: refs.useByteplusForSeedance,
      byteplusAssetStatus: refs.status,
    };
  });

  return { patchedCharacters: patched, patchedIds, consideredCount: candidates.length };
};
